import { sortAlphabetical } from '../../../utils/utils';
import { api } from '../../../services/api';
import { session } from '../../../services/session';

import Constants from '../../../app.constants';
import { dateService } from '../../../services/date';
import { getColumns } from './utils';

export class OverviewService {
  constructor() {
    this.Api = api;
    this.Session = session;

    this.baseEndpoint = `${Constants.HOST}${Constants.API_BASE_URL}`;
    this.adminEndpoint = `${this.baseEndpoint}${Constants.ADMIN_API_ENDPOINT}`;
    this.monthElapsed = dateService.monthElapsed();
    this.merchants = [];
  }

  // Queries endpoint for pub/merch overview data
  getOverviewStats(statType, params) {
    const suffixes = {
      'merchant-overview': 'merchants',
      'publisher-overview': 'publishers',
      'merch-ed-overview': 'merch_ed',
    };

    const endpoint = `${this.adminEndpoint}stats/${suffixes[statType]}/`;
    return this.Api.get(endpoint, params).then(resp => {
      if (statType !== 'merch-ed-overview') {
        return this.processOverviewStats(resp.data.data, statType, params);
      }
      return this.processMerchEdStats(resp.data.data[0], params);
    });
  }

  // Parse and normalize the data for calculations
  processOverviewStats(data, statType, params) {
    const statTemplate = {
      clicks: 0,
      impressions: 0,
      spend: 0,
      mtd_spend: 0,
      roi_goal: 0,
      cpc_goal: 0,
      monthly_budget_amount: 0,
      daily_budget_amount: 0,
    };

    const processedData = data.map(stat => {
      const normalizedStat = { ...statTemplate };
      Object.keys(normalizedStat).forEach(
        key => (normalizedStat[key] = parseFloat(stat[key]) || 0),
      );

      if (statType === 'merchant-overview') {
        // Merchant fields
        normalizedStat.percent_month_remaining = (1 - this.monthElapsed) * 100;
        normalizedStat.merchants_revenue = parseFloat(stat.revenue) || 0;
        normalizedStat.merchant_spend = stat.spend;
      } else {
        // Publisher fields
        normalizedStat.publisher_revenue = parseFloat(stat.revenue) || 0;
        normalizedStat.merchants_revenue = parseFloat(stat.merchants_revenue) || 0;
      }
      return {
        ...stat,
        ...normalizedStat,
      };
    });

    return [
      this.calculateTotals(processedData, statType, params),
      this.calculateOverviewStats(processedData, statType, params),
    ];
  }

  processMerchEdStats(data, params) {
    const { stats } = data;

    const totalsData = stats[0];
    const columns = getColumns('merch-ed-overview').map(col => col.name);

    const merchData = stats[1].map(stat => {
      const parsedStat = {};
      columns.forEach(key => {
        parsedStat[key] = key !== 'short_name' ? parseFloat(stat[key]) : stat[key];
      });
      const {
        merch_id,
        budget_id,
        is_active,
        is_reconciled,
        merchant_roi_goals,
        guardrail_lower_bound,
        guardrail_upper_bound,
      } = stat;
      return {
        ...parsedStat,
        merch_id,
        budget_id,
        is_active,
        is_reconciled,
        merchant_roi_goals,
        guardrail_lower_bound: parseFloat(guardrail_lower_bound),
        guardrail_upper_bound: parseFloat(guardrail_upper_bound),
      };
    });

    return [
      this.sortOverviewStats([totalsData], params),
      this.sortOverviewStats(merchData, params),
      data.page_info.total_items,
    ];
  }

  getMerchants() {
    return this.Api.get('/api/v0/merchants/')
      .then(resp => {
        const merchants = resp.data.data.filter(merchant => {
          return merchant.show_in_dashboard;
        });

        this.merchants = sortAlphabetical(merchants, 'short_name');
        return this.merchants;
      })
      .catch(() => []);
  }

  calculateTotals(data, statType, params) {
    const totalSummable = {};
    data.forEach(row => {
      Object.keys(row).forEach(key => {
        if (!totalSummable[key]) {
          totalSummable[key] = parseFloat(row[key]);
        } else {
          totalSummable[key] += parseFloat(row[key]);
        }
      });
    });

    totalSummable.percent_month_remaining = data[0].percent_month_remaining;
    totalSummable.short_name = 'Totals';

    return this.calculateOverviewStats([totalSummable], statType, params);
  }

  // Calculates statistics not included in the network request
  calculateOverviewStats(data, statType, params) {
    const calculatedStats = data.map(stat => {
      const calcStat = { ...stat };
      const numerators = {
        'merchant-overview': calcStat.spend,
        'publisher-overview': calcStat.publisher_revenue,
      };

      calcStat.pacing_towards = this.projectValue(numerators[statType]);
      calcStat.average_cpc = calcStat.clicks ? numerators[statType] / calcStat.clicks : 0;
      calcStat.cpc_variance = calcStat.cpc_goal
        ? calcStat.cpc - calcStat.cpc_goal / calcStat.cpc_goal
        : 0;
      calcStat.click_through_rate = calcStat.impressions
        ? (calcStat.clicks / calcStat.impressions) * 100
        : 0;
      calcStat.average_roi = calcStat.spend ? calcStat.merchants_revenue / calcStat.spend : 0;
      calcStat.roi_variance =
        calcStat.roi_goal && calcStat.average_roi
          ? (calcStat.average_roi - calcStat.roi_goal) / calcStat.roi_goal
          : 0;

      calcStat.percent_spend_remaining = calcStat.monthly_budget_amount
        ? ((calcStat.monthly_budget_amount - calcStat.mtd_spend) / calcStat.monthly_budget_amount) *
          100
        : 0;

      return calcStat;
    });

    return this.sortOverviewStats(calculatedStats, params);
  }

  sortOverviewStats(data, params, sortedColumn = '') {
    let direction;

    if (sortedColumn) {
      if (params.order_by === sortedColumn) {
        direction = params.direction === 'desc' ? 'asc' : 'desc';
      } else {
        direction = 'desc';
      }
    } else {
      sortedColumn = params.order_by;
      direction = params.direction;
    }

    data.sort((a, b) => {
      if (a[sortedColumn] <= b[sortedColumn]) {
        return direction === 'desc' ? 1 : -1;
      }
      return direction === 'desc' ? -1 : 1;
    });

    return {
      data,
      params: { ...params, order_by: sortedColumn, direction },
    };
  }

  projectValue(currentValue) {
    if (!currentValue) return 0;

    // Round up to the 2nd decimal value
    const projectedValue = Math.round((currentValue / this.monthElapsed) * 100) / 100;
    return parseFloat(projectedValue.toFixed(2));
  }

  // Only monthly budget is used in the new budget system. To prevent bad data in the DB causing issues if we have to
  // roll back the budget changes, update daily budget with the monthly value when updating the budget
  updateBudget(merchId, value, type, budgetId, date) {
    const yearMonth = dateService.formatYearMonth(date); // YYYY-MM

    const dailyEndpoint = `${this.baseEndpoint}merchants/${merchId}/`;
    const monthlyEndpoint = `${this.baseEndpoint}merchants/${merchId}/budgets/${
      budgetId ? `${budgetId}/` : ''
    }`;

    const dailyParams = {
      daily_budget_amount: value,
    };
    const monthlyParams = {
      budget_amount: value,
      year_month: yearMonth,
    };

    if (!budgetId) {
      return this.Api.post(monthlyEndpoint, monthlyParams);
    }

    // Update daily budget with monthly value in case we need to roll back to using daily budgets
    this.Api.put(dailyEndpoint, dailyParams);

    // Update monthly budget
    return this.Api.put(monthlyEndpoint, monthlyParams);
  }

  updateMerchRecon(merchIds, reconAction, guardrailLowerBound, guardrailUpperBound) {
    const endpoint = `${this.baseEndpoint}update_merchant_reconciliation/`;
    const params = {
      is_reconciled: reconAction,
      merch_ids: merchIds,
    };
    if (reconAction) {
      params.guardrail_lower_bound = guardrailLowerBound;
      params.guardrail_upper_bound = guardrailUpperBound;
    }
    return this.Api.put(endpoint, params);
  }
}

export default new OverviewService();
